import React from 'react';
import { MBView, MBViewPadding } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBAnimation } from '@shared/components/animation';
import { TOOLTIP_TRANSITION_MS } from './constants';
import { Styles } from './styles';
import { MBTooltipHorizontalArrowAlignment, MBTooltipVerticalArrowAlignment } from './types';

export interface MBTooltipProps {
  children?: React.ReactNode | React.ReactNode[];
  horizontalArrowAlignment?: MBTooltipHorizontalArrowAlignment;
  verticalArrowAlignment?: MBTooltipVerticalArrowAlignment;
  showArrow?: boolean;
  theme?: 'white' | 'dark' | 'purple';
  padding?: MBViewPadding;
}

/*
    Tooltip component that can be absolutely positioned to overlay actual content.
    Tooltips can show with the arrow pointing down or up. The arrow can be aligned
    to the left, center or right of the tooltip box.
*/
export function MBTooltip({
  children,
  padding = 1.25,
  theme = 'white',
  verticalArrowAlignment = 'below',
  horizontalArrowAlignment = 'left',
  showArrow = false,
}: MBTooltipProps) {
  const isBelow = verticalArrowAlignment === 'below';
  const isWhite = theme === 'white';
  const isDark = theme === 'dark';
  const isPurple = theme === 'purple';

  // Make the tooltip animate in from below or above
  const translateY = isBelow ? 10 : -10;
  // Flip the order of the content and the tooltip arrow if the arrow should be above
  const flexDirection = isBelow ? 'column' : 'column-reverse';

  return (
    <MBAnimation
      opacity={{ from: 0, to: 1 }}
      translateY={{ from: translateY, to: 0 }}
      duration={TOOLTIP_TRANSITION_MS}
      style={{ flexDirection, paddingHorizontal: 5 }}
    >
      <MBView
        padding={padding}
        style={[
          Styles.container,
          isWhite && Styles.themeWhite,
          isDark && Styles.themeDark,
          isPurple && Styles.themePurple,
          horizontalArrowAlignment === 'left' && { alignSelf: 'flex-start' },
          horizontalArrowAlignment === 'right' && { alignSelf: 'flex-end' },
          horizontalArrowAlignment === 'center' && { alignSelf: 'center' },
        ]}
      >
        {children}
      </MBView>
      {showArrow && (
        <View centerHorizontally>
          <MBView
            style={[
              Styles.arrow,
              isWhite && Styles.arrowWhite,
              isDark && Styles.arrowDark,
              isPurple && Styles.arrowPurple,
              isBelow ? Styles.arrowDown : Styles.arrowUp,
            ]}
          />
        </View>
      )}
    </MBAnimation>
  );
}
